import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Plus } from '@digital-twins/design-system/icons'
import {
  LIBRARY_SEARCH_GROUPS,
  LibraryCollectionItem,
  LibraryCollectionRules,
  LibraryTopicBrandRule,
  LibrarySearchBrand,
  LibrarySearchCollection,
  LibrarySearchGroup,
  LibrarySearchResponse,
  LibrarySearchTheme,
  LibrarySearchTopic,
} from '@digital-twins/shared-types'

import { AudienceCollectionCard } from '@/components/library/AudienceCollectionCard'
import { CollectionResourceCard } from '@/components/library/CollectionResourceCard'
import { StudyCollectionCard } from '@/components/library/StudyCollectionCard'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatTopicStat } from '@/pages/KnowledgeHubPage/social/SocialStatRow'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

import { LIBRARY_COLLECTION_GRID_CLASS, LibraryAreaTitle } from './LibraryCollectionsGrid'
import { useLibrarySearchGroup } from './useLibrarySearch'

export interface LibraryGroupedSearchResultsProps {
  query: string
  results: LibrarySearchResponse | null
  isLoading: boolean
  /** The grouped read failed: a muted error line replaces the (possibly stale) groups. */
  hasError?: boolean
  /** Opens the brand's collection, or New collection prefilled with its tokens. */
  onOpenBrand: (brand: LibrarySearchBrand) => void
}

/** The server returns up to this many results per group; a full group may have more. */
export const LIBRARY_SEARCH_GROUP_PREVIEW = 5

/**
 * Resource kinds the grouped view shows in its preview. Documents and datasets
 * are document hits, which keep today's rich result list in the Resources tab,
 * so the preview leaves them out to show each document once. "See all" pages
 * every kind. Audiences are not Library resources (Audiences tab only).
 */
const GROUPED_PREVIEW_RESOURCE_KINDS: ReadonlySet<LibraryCollectionItem['kind']> = new Set(['study'])

/**
 * The rules a brand result prefills New collection with. A brand token is a
 * facet value token (for `brands`) or a Topic brand pair `${topicToken}:${brandId}`
 * (for `topicBrands`, split on the first colon). Empty fields are left out.
 */
export function brandRulesFromSearchBrand(brand: LibrarySearchBrand): Partial<LibraryCollectionRules> {
  const brands: Array<string> = []
  const topicBrands: Array<LibraryTopicBrandRule> = []
  for (const token of brand.brandTokens) {
    const separator = token.indexOf(':')
    if (separator < 0) {
      brands.push(token)
      continue
    }
    const topicToken = token.slice(0, separator)
    const brandId = token.slice(separator + 1)
    if (topicToken && brandId) {
      topicBrands.push({ topicToken, brandId })
    }
  }
  const rules: Partial<LibraryCollectionRules> = {}
  if (brands.length > 0) {
    rules.brands = brands
  }
  if (topicBrands.length > 0) {
    rules.topicBrands = topicBrands
  }
  return rules
}

const ROW_CLASS =
  'group flex min-w-0 items-center justify-between gap-3 rounded-xl border border-card-border bg-background px-4 py-3 text-left transition-colors hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

const ROW_GRID_CLASS = 'grid grid-cols-1 gap-2 @[760px]/library:grid-cols-2 @[1180px]/library:grid-cols-3'

interface RowTextProps {
  title: string
  meta?: ReactNode
}

function RowText(props: RowTextProps) {
  const { title, meta } = props

  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-[15px] font-medium text-foreground">{title}</span>
      {meta ? <span className="truncate text-[12px] tabular-nums text-muted-foreground">{meta}</span> : null}
    </span>
  )
}

interface GroupItemsProps<T> {
  items: Array<T>
  testIdPrefix: string
}

interface ThemeRowsProps extends GroupItemsProps<LibrarySearchTheme> {
  language: string
}

function ThemeRows(props: ThemeRowsProps) {
  const { items, testIdPrefix, language } = props

  const { t } = useTranslation('app')

  return (
    <div className={ROW_GRID_CLASS}>
      {items.map((theme, index) => (
        <div key={theme.token} className={ROW_CLASS} data-testid={`${testIdPrefix}-${index}`}>
          <RowText
            title={theme.name}
            meta={`${t('library.collections.topics.posts')} ${formatTopicStat(theme.posts, language)}`}
          />
        </div>
      ))}
    </div>
  )
}

function TopicRows(props: GroupItemsProps<LibrarySearchTopic>) {
  const { items, testIdPrefix } = props

  const { t } = useTranslation('app')

  return (
    <div className={ROW_GRID_CLASS}>
      {items.map((topic, index) => (
        <Link
          key={topic.token}
          href={ROUTES.knowledgeTopic(topic.token)}
          className={ROW_CLASS}
          data-testid={`${testIdPrefix}-${index}`}
        >
          <RowText
            title={topic.name}
            meta={
              topic.collectionCount > 0
                ? t('library.collections.topics.inCollections', { count: topic.collectionCount })
                : null
            }
          />
          <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Link>
      ))}
    </div>
  )
}

function CollectionRows(props: GroupItemsProps<LibrarySearchCollection>) {
  const { items, testIdPrefix } = props

  const { t } = useTranslation('app')

  return (
    <div className={ROW_GRID_CLASS}>
      {items.map((collection, index) => (
        <Link
          key={collection.token}
          href={ROUTES.knowledgeCollection(collection.token)}
          className={ROW_CLASS}
          data-testid={`${testIdPrefix}-${index}`}
        >
          <RowText title={collection.name} meta={t(`library.collections.presets.${collection.preset}`)} />
          <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Link>
      ))}
    </div>
  )
}

interface BrandRowsProps extends GroupItemsProps<LibrarySearchBrand> {
  onOpenBrand: (brand: LibrarySearchBrand) => void
}

function BrandRows(props: BrandRowsProps) {
  const { items, testIdPrefix, onOpenBrand } = props

  const { t } = useTranslation('app')

  return (
    <div className={ROW_GRID_CLASS}>
      {items.map((brand, index) => {
        const hasCollection = brand.collectionToken != null
        return (
          <button
            key={`${brand.name}-${index}`}
            type="button"
            className={ROW_CLASS}
            onClick={() => onOpenBrand(brand)}
            data-testid={`${testIdPrefix}-${index}`}
            data-has-collection={hasCollection ? 'true' : 'false'}
          >
            <RowText
              title={brand.name}
              meta={hasCollection ? t('library.collections.presets.brand') : t('library.collections.newCollection')}
            />
            {hasCollection ? (
              <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
          </button>
        )
      })}
    </div>
  )
}

function ResourceCards(props: GroupItemsProps<LibraryCollectionItem>) {
  const { items } = props

  return (
    <div className={LIBRARY_COLLECTION_GRID_CLASS}>
      {items.map((item, index) => {
        if (item.kind === 'study') {
          return <StudyCollectionCard key={`${item.kind}-${item.token}`} item={item} index={index} />
        }
        if (item.kind === 'audience') {
          return <AudienceCollectionCard key={`${item.kind}-${item.token}`} item={item} index={index} />
        }
        if (item.kind === 'document' || item.kind === 'dataset') {
          return <CollectionResourceCard key={`${item.kind}-${item.token}`} item={item} index={index} />
        }
        return null
      })}
    </div>
  )
}

interface GroupBodyProps {
  group: LibrarySearchGroup
  items: Array<unknown>
  onOpenBrand: (brand: LibrarySearchBrand) => void
  language: string
}

function GroupBody(props: GroupBodyProps) {
  const { group, items, onOpenBrand, language } = props

  const testIdPrefix = `library-search-${group}`

  if (group === 'themes') {
    return (
      <ThemeRows items={items as Array<LibrarySearchTheme>} testIdPrefix={testIdPrefix} language={language} />
    )
  }
  if (group === 'topics') {
    return <TopicRows items={items as Array<LibrarySearchTopic>} testIdPrefix={testIdPrefix} />
  }
  if (group === 'collections') {
    return <CollectionRows items={items as Array<LibrarySearchCollection>} testIdPrefix={testIdPrefix} />
  }
  if (group === 'brands') {
    return (
      <BrandRows items={items as Array<LibrarySearchBrand>} testIdPrefix={testIdPrefix} onOpenBrand={onOpenBrand} />
    )
  }
  return <ResourceCards items={items as Array<LibraryCollectionItem>} testIdPrefix={testIdPrefix} />
}

interface ExpandedGroupProps {
  query: string
  group: LibrarySearchGroup
  onOpenBrand: (brand: LibrarySearchBrand) => void
  language: string
}

/** "See all" for one group: every result, a page at a time, with Show more. */
function ExpandedGroup(props: ExpandedGroupProps) {
  const { query, group, onOpenBrand, language } = props

  const { t } = useTranslation('app')
  const page = useLibrarySearchGroup(query, group)

  if (page.isLoading && page.items.length === 0) {
    return <RowsSkeleton testId={`library-search-group-${group}-loading`} />
  }
  if (page.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`library-search-group-${group}-empty`}>
        {t('library.collections.search.noResults', { query })}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <GroupBody group={group} items={page.items} onOpenBrand={onOpenBrand} language={language} />
      {page.hasMore ? (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            shape="pill"
            className="h-8 px-3"
            disabled={page.isLoadingMore}
            onClick={page.loadMore}
            data-testid={`library-search-group-${group}-show-more`}
          >
            {t('library.collections.showMore')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

interface RowsSkeletonProps {
  testId: string
}

function RowsSkeleton(props: RowsSkeletonProps) {
  const { testId } = props

  return (
    <div
      className={`${ROW_GRID_CLASS} animate-in fade-in fill-mode-both delay-150`}
      aria-hidden="true"
      data-testid={testId}
    >
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="h-[62px] rounded-xl border border-card-border bg-background" />
      ))}
    </div>
  )
}

/** The preview items a group shows before "See all". */
function previewItems(group: LibrarySearchGroup, results: LibrarySearchResponse): Array<unknown> {
  if (group === 'resources') {
    return results.resources.filter((item) => GROUPED_PREVIEW_RESOURCE_KINDS.has(item.kind))
  }
  return results[group]
}

/**
 * The grouped Library search (D16): themes, Topics, collections, brands and
 * resources, up to 5 each, in that order. A group with no results renders
 * nothing (themes stay `[]` until Phase B). "See all" pages one group in
 * place through `GET /library/search?q=&group=&cursor=`. A brand result calls
 * `onOpenBrand`. A failed read shows a muted error line. With every group
 * empty it renders nothing: the document results below carry the empty state.
 */
export function LibraryGroupedSearchResults(props: LibraryGroupedSearchResultsProps) {
  const { query, results, isLoading, hasError = false, onOpenBrand } = props

  const { t, i18n } = useTranslation('app')
  const [expandedGroup, setExpandedGroup] = useState<LibrarySearchGroup | null>(null)

  useEffect(() => {
    setExpandedGroup(null)
  }, [query])

  if (hasError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="library-search-error">
        {t('library.collections.search.loadFailed')}
      </p>
    )
  }

  if (!results) {
    return isLoading ? (
      <section className="@container/library" data-testid="library-search-loading">
        <RowsSkeleton testId="library-search-loading-rows" />
      </section>
    ) : null
  }

  const groups = LIBRARY_SEARCH_GROUPS.map((group) => {
    const all = results[group] as Array<unknown>
    const preview = previewItems(group, results)
    const hasMore = all.length >= LIBRARY_SEARCH_GROUP_PREVIEW
    return { group, preview, hasMore }
  }).filter(({ group, preview, hasMore }) => preview.length > 0 || (group === 'resources' && hasMore))

  if (groups.length === 0) {
    return null
  }

  return (
    <section
      className="@container/library flex min-w-0 flex-col gap-7"
      data-testid="library-search-results"
    >
      {groups.map(({ group, preview, hasMore }) => {
        const isExpanded = expandedGroup === group
        const seeAll =
          hasMore && !isExpanded ? (
            <button
              type="button"
              className="rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setExpandedGroup(group)}
              data-testid={`library-search-group-${group}-see-all`}
            >
              {t('library.collections.seeAll')}
            </button>
          ) : null
        return (
          <div key={group} className="flex min-w-0 flex-col gap-3" data-testid={`library-search-group-${group}`}>
            <LibraryAreaTitle
              title={t(`library.collections.search.groups.${group}`)}
              trailing={seeAll}
              testId={`library-search-group-${group}-header`}
            />
            {isExpanded ? (
              <ExpandedGroup query={query} group={group} onOpenBrand={onOpenBrand} language={i18n.language} />
            ) : (
              <GroupBody group={group} items={preview} onOpenBrand={onOpenBrand} language={i18n.language} />
            )}
          </div>
        )
      })}
    </section>
  )
}
